// 바로 뜨는 말풍선(2026-10-28 사용자: "기본 툴팁 말고 바로 호버되게, 사이트 전체에서 기본 동작 걷어내기").
// - 브라우저 title 말풍선은 1초쯤 늦게 뜬다 → 사이트 전체의 title을 마우스가 올라오는 순간 가로채서 이 말풍선으로 보여 준다
//   (title은 data-tip으로 옮겨 기본 말풍선이 안 뜨게, 글자 없는 버튼엔 aria-label을 채워 화면 읽기 프로그램용 이름을 유지).
// - 화면 고정(fixed)이라 가로 스크롤 상자 안에서도 잘리지 않는다. 마우스에서만(터치는 탭할 때 남아 있는 게 거슬려서 제외).
// - 코드에서 직접 쓰려면 {...tip('글자')} (생산 시간 판처럼 상태에 따라 글자가 바뀌는 곳).
// App에 <HoverTip global={어드민} />를 한 번 둔다 — title 가로채기는 어드민 로그인 상태에서만(2026-10-28 사용자: "어드민한테만").
import React, { useEffect, useSyncExternalStore } from 'react';

type Tip = { text: string; x: number; y: number } | null;
let current: Tip = null;
const subs = new Set<() => void>();
const set = (t: Tip) => {
  current = t;
  subs.forEach((f) => f());
};

export const tip = (text: string) => ({
  onMouseEnter: (e: React.MouseEvent) => set({ text, x: e.clientX, y: e.clientY }),
  onMouseMove: (e: React.MouseEvent) => set({ text, x: e.clientX, y: e.clientY }),
  onMouseLeave: () => set(null),
});

/** title → data-tip(처음 올라올 때 한 번). React가 title을 새 값으로 다시 넣으면 다음에 또 옮긴다 */
const takeTitle = (el: Element) => {
  const t = el.getAttribute('title');
  if (t) {
    el.setAttribute('data-tip', t);
    el.removeAttribute('title');
    if (!el.getAttribute('aria-label') && !(el.textContent ?? '').trim()) el.setAttribute('aria-label', t);
  }
  return el.getAttribute('data-tip');
};

export const HoverTip: React.FC<{ global?: boolean }> = ({ global = false }) => {
  const t = useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => current,
  );
  useEffect(() => {
    if (!global) return; // 사이트 전체 가로채기는 어드민만(2026-10-28 사용자), {...tip()}로 직접 단 곳은 늘
    let el: Element | null = null;
    const over = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      const target = (e.target as Element | null)?.closest?.('[title],[data-tip]') ?? null;
      if (!target) return;
      el = target;
      const text = takeTitle(target);
      if (text) set({ text, x: e.clientX, y: e.clientY });
    };
    const move = (e: PointerEvent) => {
      if (el && current && e.pointerType === 'mouse') set({ ...current, x: e.clientX, y: e.clientY });
    };
    const out = (e: PointerEvent) => {
      if (!el) return;
      const to = e.relatedTarget as Node | null;
      if (to && el.contains(to)) return;
      el = null;
      set(null);
    };
    const hide = () => {
      el = null;
      if (current) set(null);
    };
    document.addEventListener('pointerover', over);
    document.addEventListener('pointermove', move);
    document.addEventListener('pointerout', out);
    document.addEventListener('pointerdown', hide);
    window.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('pointerdown', hide);
      window.removeEventListener('scroll', hide, true);
    };
  }, [global]);
  if (!t) return null;
  // 커서 오른쪽 아래, 화면 끝에 닿으면 안쪽으로
  const left = Math.max(4, Math.min(t.x + 14, window.innerWidth - 264));
  const top = t.y + 40 > window.innerHeight ? t.y - 32 : t.y + 16;
  return (
    <div className="pointer-events-none fixed z-[1000] max-w-[260px] whitespace-pre-line rounded border border-white/20 bg-black/90 px-2 py-1 text-xs text-white shadow-lg" style={{ left, top }}>
      {t.text}
    </div>
  );
};
